import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'bulma-toast';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import styles from './styles.scss';
import { flattenChildren } from '../../../../utils/react-children-utils/react-children-utils';
import IconText from '../../../molecules/icon-text';
import ClickPopover from '../../../molecules/click-popover/ClickPopover.tsx';
import ModalContainer from '../../../molecules/modal-container/ModalContainer';

const ManageGroupView = function ManageGroupView(props) {
    const { contextkey, baseurl, children } = props;
    const { host, protocol } = new URL(baseurl);

    const [groups, setGroups] = useState([]);
    const [groupToDelete, setGroupToDelete] = useState(null);
    const [groupToUpdate, setGroupToUpdate] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);

    const useStyles = makeStyles(() => ({
        typography: {
            padding: '2px',
            color: 'green',
        },
    }));
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);

    const handleCopyURL = (pageUrl) => (event) => {
        navigator.clipboard.writeText(pageUrl);
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const openPopover = Boolean(anchorEl);
    const URLId = openPopover ? 'simple-popover' : undefined;

    function fetchGroups() {
        return fetch(`${protocol}//${host}/odata/groups?$apply=filter(ContextKey eq '${contextkey}')`)
            .then((response) => response.json())
            .then((json) => json.value)
            .then((value) => setGroups(value));
    }

    const toastConfig = {
        position: 'bottom-center',
        duration: 2000,
        closeOnClick: true,
        pauseOnHover: true,
    };

    function sendApiRequest(httpOptions, actioned, groupKey, title, updatefn) {
        return fetch(`${protocol}//${host}/odata/groups('${groupKey}')`, httpOptions)
            .then((response) => {
                if (response.status === 204) {
                    toast({
                        ...toastConfig,
                        message: `The group ${title} has been successfully ${actioned}`,
                        type: 'is-primary',
                    });

                    updatefn(groupKey);
                } else {
                    toast({
                        ...toastConfig,
                        message: `The group ${title} could not be ${actioned}. Please contact us at theteam@giveeasy.org if the issue persists`,
                        type: 'is-danger',
                    });
                }

                return response;
            });
    }

    function updateAfterDisabled(groupKey) {
        const newGroups = [...groups];

        const index = groups.findIndex((g) => g.Key === groupKey);
        const current = groups[index];

        const disabledGroup = { ...current, IsDisabled: !current.IsDisabled };

        newGroups[index] = disabledGroup;

        setGroups(newGroups);
        setGroupToUpdate(null);
    }

    function updateAfterDelete(groupKey) {
        const newGroups = [...groups];

        const index = newGroups.findIndex((g) => g.Key === groupKey);
        if (index > -1) {
            newGroups.splice(index, 1);
            setGroups(newGroups);
        }

        setGroupToDelete(null);
    }

    useEffect(() => {
        fetchGroups();
    }, []);

    const updateClickHandler = (g) => {
        setShowUpdateModal(true);
        setGroupToUpdate(g);
    };

    const deleteClickHandler = (g) => {
        setShowDeleteModal(true);
        setGroupToDelete(g);
    };

    const sendUpdateRequest = () => {
        const group = groupToUpdate;

        sendApiRequest({
            method: 'PATCH',
            body: JSON.stringify({ IsDisabled: !group.IsDisabled }),
            headers: new Headers({ 'content-type': 'application/json' }),
        }, 'updated', group.Key, group.Title, updateAfterDisabled);
    };

    const updateModal = () => {
        const isDisabled = groupToUpdate?.IsDisabled ?? false;
        const action = isDisabled ? 'enable' : 'disable';

        return (
            <ModalContainer
                showmodal={showUpdateModal}
                showheader
                showfooter
                modaltitle={`Are you sure you want to ${action} this team?`}
                modalclosebuttontext="Cancel"
                confirmbuttontext="Confirm"
                confirmaction={() => sendUpdateRequest()}
                toggleshow={() => setShowUpdateModal(false)}
            >
                <p>
                    {
                        isDisabled
                            ? 'This will allow the team to collect donations and appear on the leaderboard.'
                            : 'This will prevent the team from collecting donations and will not appear on the leaderboard'
                    }
                </p>
            </ModalContainer>
        );
    };

    const deleteModal = () => {
        const title = groupToDelete?.Title ?? '';
        return (
            <ModalContainer
                showmodal={showDeleteModal}
                showheader
                showfooter
                modaltitle={`Are you sure you want to delete the group "${title}"?`}
                modalclosebuttontext="Cancel"
                confirmbuttontext="Confirm"
                confirmaction={() => sendApiRequest({ method: 'DELETE' }, 'removed', groupToDelete.Key, groupToDelete.Title, updateAfterDelete)}
                toggleshow={() => setShowDeleteModal(false)}
            >
                <p>
                    {`Are you sure you want to delete the group "${title}"? `}
                    You cannot undo this action.
                </p>
            </ModalContainer>
        );
    };

    function getIcons(groupEntries) {
        return groupEntries.map((g) => (
            <div key={g.Key}>
                <div className={styles['name-column']}>
                    <a href={g.PageUrl} target="_blank" rel="noreferrer">
                        {g.Title}
                    </a>
                </div>
                <div className={styles['action-column']}>
                    <IconText
                        type="anchor"
                        text="Edit"
                        iconPath="M0.25 10.9375V13.75H3.0625L11.3575 5.455L8.545 2.6425L0.25 10.9375ZM2.44 12.25H1.75V11.56L8.545 4.765L9.235 5.455L2.44 12.25ZM13.5325 2.2225L11.7775 0.4675C11.6275 0.3175 11.44 0.25 11.245 0.25C11.05 0.25 10.8625 0.325 10.72 0.4675L9.3475 1.84L12.16 4.6525L13.5325 3.28C13.825 2.9875 13.825 2.515 13.5325 2.2225Z"
                        svgWidth="14"
                        svgHeight="14"
                        svgViewBox="0 0 14 14"
                        href={`${protocol}//${host}/Campaigns/Groups/Edit?groupKey=${g.Key}`}
                        iconTextStyle={styles.edit}
                    />

                    <IconText
                        type="button"
                        text="Copy URL"
                        iconPath="M10.25 0.750061H2C1.175 0.750061 0.5 1.42506 0.5 2.25006V12.0001C0.5 12.4126 0.8375 12.7501 1.25 12.7501C1.6625 12.7501 2 12.4126 2 12.0001V3.00006C2 2.58756 2.3375 2.25006 2.75 2.25006H10.25C10.6625 2.25006 11 1.91256 11 1.50006C11 1.08756 10.6625 0.750061 10.25 0.750061ZM13.25 3.75006H5C4.175 3.75006 3.5 4.42506 3.5 5.25006V15.7501C3.5 16.5751 4.175 17.2501 5 17.2501H13.25C14.075 17.2501 14.75 16.5751 14.75 15.7501V5.25006C14.75 4.42506 14.075 3.75006 13.25 3.75006ZM12.5 15.7501H5.75C5.3375 15.7501 5 15.4126 5 15.0001V6.00006C5 5.58756 5.3375 5.25006 5.75 5.25006H12.5C12.9125 5.25006 13.25 5.58756 13.25 6.00006V15.0001C13.25 15.4126 12.9125 15.7501 12.5 15.7501Z"
                        svgWidth="15"
                        svgHeight="18"
                        svgViewBox="0 0 15 18"
                        clickHandler={handleCopyURL(g.PageUrl)}
                        iconTextStyle={styles.copy}
                    />
                    <IconText
                        testid={`${g.Key}-remove`}
                        type="button"
                        text="Remove"
                        iconPath="M9 4.75V12.25H3V4.75H9ZM7.875 0.25H4.125L3.375 1H0.75V2.5H11.25V1H8.625L7.875 0.25ZM10.5 3.25H1.5V12.25C1.5 13.075 2.175 13.75 3 13.75H9C9.825 13.75 10.5 13.075 10.5 12.25V3.25Z"
                        svgWidth="12"
                        svgHeight="14"
                        svgViewBox="0 0 12 14"
                        iconTextStyle={styles.remove}
                        clickHandler={() => deleteClickHandler(g)}
                    />

                    <IconText
                        testid={g.IsDisabled ? `${g.Key}-enable` : `${g.Key}-disable`}
                        type="button"
                        text={g.IsDisabled ? 'Enable' : 'Disable'}
                        iconPath="M12.75 0.5H5.25C2.7675 0.5 0.75 2.5175 0.75 5C0.75 7.4825 2.7675 9.5 5.25 9.5H12.75C15.2325 9.5 17.25 7.4825 17.25 5C17.25 2.5175 15.2325 0.5 12.75 0.5ZM12.75 8H5.25C3.5925 8 2.25 6.6575 2.25 5C2.25 3.3425 3.5925 2 5.25 2H12.75C14.4075 2 15.75 3.3425 15.75 5C15.75 6.6575 14.4075 8 12.75 8ZM12.75 2.75C11.505 2.75 10.5 3.755 10.5 5C10.5 6.245 11.505 7.25 12.75 7.25C13.995 7.25 15 6.245 15 5C15 3.755 13.995 2.75 12.75 2.75Z"
                        svgWidth="18"
                        svgHeight="10"
                        svgViewBox="0 0 18 10"
                        iconTextStyle={g.IsDisabled ? styles.disable : styles.enable}
                        clickHandler={() => updateClickHandler(g)}
                    />
                </div>
            </div>
        ));
    }

    const formError = flattenChildren(children).find((c) => c.props && c.props['data-valmsg-summary']);
    const popoverContent = (
        <Typography className={classes.typography} component="div">
            <div>
                <CheckCircleIcon style={{ paddingTop: '8px' }} />
                URL Copied
            </div>
        </Typography>
    );
    const anchorOrigin = {
        vertical: 'center',
        horizontal: 'right',
    };
    const transformOrigin = {
        vertical: 'top',
        horizontal: 'left',
    };

    return (
        <div className={`${styles['manage-group']} content is-centered`}>
            <div className={styles.error}>
                {formError}
            </div>
            <ClickPopover
                URLId={URLId}
                openPopover={openPopover}
                anchorEl={anchorEl}
                handleClose={handleClose}
                message={popoverContent}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
            />
            {deleteModal()}
            {updateModal()}
            <div className={styles['group-table']}>
                <div className={styles['group-header']}>
                    <span className={styles['name-column']}>Teams</span>
                    <span className={styles['action-column']}>Actions</span>
                </div>
                <div className={styles['group-body']}>
                    {
                        groups.length > 0
                            ? getIcons(groups)
                            : (
                                <div>
                                    <div className={styles['name-column']} />
                                    <div className={styles['action-column']} />
                                </div>
                            )
                    }
                </div>
            </div>
        </div>
    );
};

ManageGroupView.propTypes = {
    children: PropTypes.node.isRequired,
    contextkey: PropTypes.string.isRequired,
    baseurl: PropTypes.string,
};

ManageGroupView.defaultProps = {
    baseurl: window.location.href,
};

export default ManageGroupView;
